import { alpha, palette, radius, semantic, shadow, size, typography } from './foundations';

const { brand, neutral } = palette;
const white = neutral[0];

// Every form control shares one interaction model:
// neutral border → stronger neutral on hover → brand border + focus ring on focus.
// (The focus ring itself comes from the global controlOutline token.)
const controlStates = {
  hoverBorderColor: semantic.border.strong,
  activeBorderColor: brand[500],
};

const components = {
  /* ---------------------------- Actions ---------------------------- */
  Button: {
    fontWeight: typography.weight.semibold,
    paddingInline: 14,
    primaryShadow: `0 1px 2px ${alpha(brand[700], 0.24)}`,
    dangerShadow: `0 1px 2px ${alpha(palette.error, 0.24)}`,
    defaultShadow: shadow.xs,
    defaultColor: semantic.text.primary,
    defaultBorderColor: semantic.border.default,
    defaultHoverColor: semantic.text.heading,
    defaultHoverBg: semantic.state.hover,
    defaultHoverBorderColor: semantic.border.strong,
    defaultActiveColor: semantic.text.heading,
    defaultActiveBg: semantic.state.pressed,
    defaultActiveBorderColor: semantic.border.strong,
    textHoverBg: semantic.state.hover,
    linkHoverBg: 'transparent',
  },

  /* ----------------------------- Inputs ---------------------------- */
  Input: {
    ...controlStates,
    paddingInline: 12,
    addonBg: semantic.surface.subtle,
  },
  InputNumber: {
    ...controlStates,
    paddingInline: 12,
    addonBg: semantic.surface.subtle,
    handleHoverColor: brand[500],
  },
  Select: {
    ...controlStates,
    optionActiveBg: semantic.state.hover,
    optionSelectedBg: semantic.state.selected,
    optionSelectedColor: brand[700],
    optionSelectedFontWeight: typography.weight.semibold,
    multipleItemBg: neutral[100],
    multipleItemBorderColor: 'transparent',
  },
  TreeSelect: {
    nodeHoverBg: semantic.state.hover,
    nodeSelectedBg: semantic.state.selected,
  },
  DatePicker: {
    ...controlStates,
    cellHoverBg: semantic.state.hover,
    cellActiveWithRangeBg: semantic.state.selected,
    cellRangeBorderColor: brand[200],
  },
  Form: {
    labelColor: semantic.text.primary,
    labelFontSize: typography.size.base,
    labelRequiredMarkColor: palette.error,
    verticalLabelPadding: '0 0 6px',
    itemMarginBottom: 20,
  },

  /* --------------------------- Data display ------------------------ */
  Table: {
    headerBg: semantic.surface.subtle,
    headerColor: semantic.text.secondary,
    headerSplitColor: 'transparent',
    headerBorderRadius: radius.md,
    headerSortActiveBg: neutral[100],
    headerSortHoverBg: neutral[100],
    bodySortBg: neutral[25],
    borderColor: semantic.border.subtle,
    rowHoverBg: neutral[25],
    rowSelectedBg: semantic.state.selected,
    rowSelectedHoverBg: semantic.state.selectedHover,
    cellPaddingBlock: 12,
    cellPaddingInline: 16,
    cellPaddingBlockSM: 8,
    cellPaddingInlineSM: 12,
    footerBg: semantic.surface.subtle,
  },
  Card: {
    headerBg: 'transparent',
    headerFontSize: typography.size.lg,
    headerFontSizeSM: typography.size.base,
    headerHeight: 52,
    headerHeightSM: 40,
    headerPadding: 20,
    headerPaddingSM: 12,
    bodyPadding: 20,
    bodyPaddingSM: 12,
    actionsBg: semantic.surface.subtle,
    extraColor: semantic.text.secondary,
  },
  Descriptions: {
    titleColor: semantic.text.heading,
    labelColor: semantic.text.secondary,
    labelBg: semantic.surface.subtle,
    contentColor: semantic.text.primary,
    itemPaddingBottom: 12,
  },
  Tag: {
    defaultBg: neutral[50],
    defaultColor: semantic.text.primary,
    borderRadiusSM: radius.sm,
  },
  Progress: {
    defaultColor: brand[500],
    remainingColor: neutral[100],
    lineBorderRadius: radius.full,
    circleTextFontSize: '1em',
  },
  Tooltip: {
    colorBgSpotlight: semantic.surface.spotlight,
    borderRadius: radius.sm,
    fontSize: typography.size.sm,
  },

  /* --------------------------- Navigation -------------------------- */
  Layout: {
    bodyBg: semantic.surface.canvas,
    headerBg: semantic.surface.container,
    headerHeight: size.header,
    headerPadding: '0 24px',
    siderBg: semantic.surface.sider,
    lightSiderBg: semantic.surface.container,
    triggerBg: neutral[950],
    triggerColor: alpha(white, 0.72),
  },
  Menu: {
    itemHeight: 44,
    itemBorderRadius: radius.md,
    itemMarginInline: 10,
    itemMarginBlock: 4,
    iconSize: 20,
    collapsedIconSize: 20,
    iconMarginInlineEnd: 14,
    activeBarBorderWidth: 0,

    // Light menu (top nav, light sider, dropdown menus)
    itemColor: semantic.text.secondary,
    itemHoverColor: semantic.text.heading,
    itemHoverBg: semantic.state.hover,
    itemActiveBg: semantic.state.pressed,
    itemSelectedColor: brand[600],
    itemSelectedBg: semantic.state.selected,
    subMenuItemBg: 'transparent',
    groupTitleColor: semantic.text.tertiary,

    // Dark menu — use <Menu theme="dark"> inside the dark sider
    darkItemBg: semantic.surface.sider,
    darkSubMenuItemBg: neutral[950],
    darkPopupBg: semantic.surface.sider,
    darkItemColor: alpha(white, 0.68),
    darkItemHoverColor: white,
    darkItemHoverBg: alpha(white, 0.06),
    darkItemSelectedColor: white,
    darkItemSelectedBg: brand[500],
    darkGroupTitleColor: alpha(white, 0.45),
  },
  Tabs: {
    itemColor: semantic.text.secondary,
    itemHoverColor: semantic.text.heading,
    itemActiveColor: semantic.text.heading,
    itemSelectedColor: brand[600],
    inkBarColor: brand[500],
    titleFontSize: typography.size.base,
    titleFontSizeSM: typography.size.sm,
    titleFontSizeLG: typography.size.lg,
    horizontalItemGutter: 24,
    horizontalItemPadding: '14px 4px',
    horizontalItemPaddingSM: '10px 2px',
    horizontalItemPaddingLG: '16px 6px',
    horizontalMargin: '0 0 20px 0',
  },
  Segmented: {
    trackBg: neutral[100],
    itemColor: semantic.text.secondary,
    itemHoverColor: semantic.text.heading,
    itemSelectedBg: white,
    itemSelectedColor: semantic.text.heading,
  },
  Pagination: {
    itemBg: 'transparent',
    itemActiveBg: semantic.state.selected,
  },
  Breadcrumb: {
    itemColor: semantic.text.tertiary,
    linkColor: semantic.text.secondary,
    linkHoverColor: semantic.text.heading,
    lastItemColor: semantic.text.heading,
    separatorColor: semantic.text.tertiary,
  },

  /* ---------------------------- Overlays --------------------------- */
  Modal: {
    contentBg: semantic.surface.elevated,
    headerBg: semantic.surface.elevated,
    footerBg: 'transparent',
    titleColor: semantic.text.heading,
    titleFontSize: typography.size.h4,
    titleLineHeight: 1.5,
    borderRadiusLG: radius.xl,
    boxShadow: shadow.xl,
  },
};

export default components;
